import { Reorder, useDragControls } from "framer-motion";
import { AlignLeft, CheckSquare, List, Circle, GripVertical, Pencil, Ticket, Trash2, ChevronUp, ChevronDown, MessageSquareText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { getCheckoutFieldTypeLabel } from "@/lib/checkout-form";
import { cn } from "@/lib/utils";
import {
  panelAlert,
  panelPill,
  panelRow,
} from "@/components/producer/panel";
import type { TierPreview } from "./types";

export function QuestionRow({
  question,
  index,
  total,
  tiers,
  onMove,
  onEdit,
  onDelete,
  onOpenAnswers,
  reorderDisabled,
}: {
  question: any;
  index: number;
  total: number;
  tiers: TierPreview[];
  onMove: (index: number, delta: number) => void;
  onEdit: (question: any) => void;
  onDelete: (question: any) => void;
  onOpenAnswers: (question: any) => void;
  reorderDisabled: boolean;
}) {
  const dragControls = useDragControls();
  const assignedTierNames = (question.tier_ids || [])
    .map((tierId: string) => tiers.find((tier) => tier.id === tierId)?.name)
    .filter(Boolean);

  return (
    <Reorder.Item
      value={question}
      id={question.id}
      dragListener={false}
      dragControls={dragControls}
      onClick={() => onEdit(question)}
      className={cn(panelRow, "group relative cursor-pointer transition-colors duration-150 ease-out hover:bg-secondary/30")}
      whileDrag={{ scale: 1.01, zIndex: 10, boxShadow: "0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)" }}
      layout
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between p-4">
        
        {/* Drag Handle & Info */}
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <button
            type="button"
            aria-label="Reordenar pergunta"
            onPointerDown={(event) => {
              event.stopPropagation();
              dragControls.start(event);
            }}
            disabled={reorderDisabled}
            className="inline-flex h-10 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground/40 hover:text-foreground hover:bg-muted transition-colors disabled:cursor-not-allowed group-hover:text-muted-foreground"
          >
            <GripVertical className="h-5 w-5" />
          </button>

          <div className="min-w-0 flex-1 space-y-1.5">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-base font-semibold text-foreground truncate max-w-full">{question.question}</p>
              {question.is_required && <span className="shrink-0 text-sm font-medium text-primary">*</span>}
            </div>
            
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium bg-muted px-1.5 py-0.5 rounded flex items-center gap-1 shrink-0">
                {question.field_type === 'text' || question.field_type === 'textarea' ? <AlignLeft className="w-3 h-3" /> :
                 question.field_type === 'checkbox' ? <CheckSquare className="w-3 h-3" /> :
                 question.field_type === 'radio' ? <Circle className="w-3 h-3" /> :
                 <List className="w-3 h-3" />}
                {getCheckoutFieldTypeLabel(question.field_type)}
              </span>
              
              <span className="text-muted-foreground/60 shrink-0">•</span>
              
              <span className="flex items-center gap-1 shrink-0">
                {question.applies_to === "attendee" ? (
                  <><Ticket className="w-3 h-3 text-muted-foreground/70" /> Cada ingresso</>
                ) : (
                  "Todo pedido"
                )}
              </span>

              {assignedTierNames.length > 0 && (
                <>
                  <span className="text-muted-foreground/60 shrink-0">•</span>
                  <span className="text-foreground/80 font-medium truncate max-w-[200px]" title={assignedTierNames.join(", ")}>
                    {assignedTierNames.length === 1 ? assignedTierNames[0] : assignedTierNames.length + " Ingressos"}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center gap-1 pl-12 sm:pl-0 shrink-0">
          <Button
            variant="outline"
            size="sm"
            onClick={(e) => { e.stopPropagation(); onOpenAnswers(question); }}
            className={cn("h-8 gap-1.5 text-xs text-muted-foreground hover:text-foreground", panelPill)}
            title="Ver respostas desta pergunta"
          >
            <MessageSquareText className="h-3.5 w-3.5 text-muted-foreground" />
            <span className="hidden sm:inline">Respostas</span>
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onEdit(question); }}
            className="text-muted-foreground hover:text-foreground hover:bg-muted"
            title="Editar"
          >
            <Pencil className="h-4 w-4" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onMove(index, -1); }}
            disabled={index === 0 || reorderDisabled}
            className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
            title="Mover acima"
          >
            <ChevronUp className="h-4 w-4" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={(e) => { e.stopPropagation(); onMove(index, 1); }}
            disabled={index === total - 1 || reorderDisabled}
            className="text-muted-foreground hover:text-foreground hidden sm:inline-flex"
            title="Mover abaixo"
          >
            <ChevronDown className="h-4 w-4" />
          </Button>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="text-muted-foreground hover:text-destructive hover:bg-destructive/10" 
                title="Remover"
                onClick={(e) => e.stopPropagation()}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className={panelAlert} onClick={(e) => e.stopPropagation()}>
              <AlertDialogHeader>
                <AlertDialogTitle className="font-display text-lg font-semibold tracking-tight">Apagar pergunta?</AlertDialogTitle>
                <AlertDialogDescription>
                  A pergunta <strong className="break-words">{question.question}</strong> será removida.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className={panelPill}>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  className={cn(panelPill, "bg-destructive text-destructive-foreground hover:bg-destructive/90")}
                  onClick={() => onDelete(question)}
                >
                  Remover
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      
      {question.options?.length > 0 && (
        <div className="px-14 pb-4 hidden sm:flex flex-wrap gap-1.5 opacity-80 group-hover:opacity-100 transition-opacity min-w-0">
          {question.options.slice(0, 4).map((option: string, i: number) => (
            <Badge key={i} variant="secondary" className="bg-muted/60 text-[10px] font-normal border-0 text-muted-foreground max-w-[150px] truncate">
              {option}
            </Badge>
          ))}
          {question.options.length > 4 && (
            <Badge variant="secondary" className="bg-muted/60 text-[10px] font-normal border-0 text-muted-foreground shrink-0">
              +{question.options.length - 4} opções
            </Badge>
          )}
        </div>
      )}
    </Reorder.Item>
  );
}
